import _ from 'lodash';

import createEnum from 'scripts/lib/create_enum';
import createModel, { prop } from 'models/lib/create_model';
import SmsMessageAttachment from './sms_message_attachment';
import redactAttachment from './attachment_redact';

const SmsMessage = createModel({
  modelName: 'SmsMessage',

  properties: {
    attachments: [SmsMessageAttachment],
    from: String,
    to: String,
    body: String,
    status: prop({
      value: String,
      errorCode: String,
    }),
    hasRedactedPaymentCardNumber: Boolean,
    sessionId: String,
  },

  redactAttachment(id) {
    redactAttachment(this.attachments, id);
  },

  getErrorMessage() {
    return this.status.errorCode ? ErrorCodeMessageMap[this.status.errorCode] : '';
  },

  hasErrorStatus() {
    return hasErrorStatus(this.getStatus());
  },

  getStatus() {
    return (this.status && this.status.value) || '';
  },

  statics: {
    create(rawAttrs) {
      const attrs = _.merge({ attachments: [] }, rawAttrs, {
        from: this._normalizeNumber(rawAttrs.from),
        to: this._normalizeNumber(rawAttrs.to),
      });
      return new this(attrs);
    },

    _normalizeNumber(number) {
      return number && number.replace(/[^\d+]/g, ''); // keeps only numbers and the plus sign
    },
  },
});

export function hasErrorStatus(status) {
  return [STATUS.FAILED, STATUS.UNDELIVERED].indexOf(status) >= 0;
}

export default SmsMessage;

export const STATUS = createEnum('NEW', 'SENDING', 'SENT', 'DELIVERED', 'UNDELIVERED', 'FAILED', 'UNKNOWN', 'RECEIVED');

// SMS error codes
export const ERROR_CODE = createEnum(
  'INVALID_TO_NUMBER',
  'MEDIA_SIZE_TOO_LARGE',
  'MISSING_TO_NUMBER',
  'MESSAGE_BODY_TOO_LONG',
  'MMS_NOT_ENABLED',
  'SEND_RATE_OVER_LIMIT',
  'SMS_ACCOUNT_SUSPENDED',
  'SMS_HANDSET_UNAVAILABLE',
  'SMS_NO_ACTIVE_CONVERSATION',
  'SMS_NUMBER_BLOCKED',
  'SMS_NUMBER_FORBIDDEN',
  'SMS_NUMBER_INVALID',
  'SMS_HANDSET_UNREACHABLE',
  'SMS_MESSAGE_OBJECTIONABLE',
  'SMS_MESSAGE_PART_NOT_RECEIVED',
  'SMS_PRICE_EXCEEDED'
);

// ErrorCodeMessageMap maps error codes to a user-facing message
const ErrorCodeMessageMap = {};
ErrorCodeMessageMap[ERROR_CODE.INVALID_TO_NUMBER] = "The 'To' number you have selected is invalid.";
ErrorCodeMessageMap[ERROR_CODE.MEDIA_SIZE_TOO_LARGE] = 'The media size exceeds the mobile operator size limit.';
ErrorCodeMessageMap[ERROR_CODE.MISSING_TO_NUMBER] = "No 'To' number specified.";
ErrorCodeMessageMap[ERROR_CODE.MESSAGE_BODY_TOO_LONG] =
  'The message body exceeded the maximum length of 1600 characters';
ErrorCodeMessageMap[ERROR_CODE.MMS_NOT_ENABLED] = 'The phone number you are sending from does not support attachments.';
ErrorCodeMessageMap[ERROR_CODE.SEND_RATE_OVER_LIMIT] =
  'You tried to send too many messages too quickly and your message queue overflowed. Try sending your message again after waiting some time.';
ErrorCodeMessageMap[ERROR_CODE.SMS_ACCOUNT_SUSPENDED] = 'Your SMS account was suspended.';
ErrorCodeMessageMap[ERROR_CODE.SMS_HANDSET_UNAVAILABLE] =
  'The destination handset you are trying to reach is switched off or otherwise unavailable.';
ErrorCodeMessageMap[ERROR_CODE.SMS_NO_ACTIVE_CONVERSATION] = 'Could not send message because conversation was closed.';
ErrorCodeMessageMap[ERROR_CODE.SMS_NUMBER_BLOCKED] = 'The destination number is blocked from receiving this message.';
ErrorCodeMessageMap[ERROR_CODE.SMS_NUMBER_FORBIDDEN] =
  "The destination number has disabled receiving messages from this number and must text 'START' to this number to enable.";
ErrorCodeMessageMap[ERROR_CODE.SMS_NUMBER_INVALID] = 'The destination number is not a valid mobile number.';
ErrorCodeMessageMap[ERROR_CODE.SMS_HANDSET_UNREACHABLE] =
  'The destination number could not be reached. It may be a landline or may no longer exist.';
ErrorCodeMessageMap[ERROR_CODE.SMS_MESSAGE_OBJECTIONABLE] = 'Your message was flagged as objectionable by the carrier.';
ErrorCodeMessageMap[ERROR_CODE.SMS_MESSAGE_PART_NOT_RECEIVED] =
  'One or more segments associated with your multi-part inbound message was not received.';
ErrorCodeMessageMap[ERROR_CODE.SMS_PRICE_EXCEEDED] = 'The price of your message exceeds the max price set.';



// WEBPACK FOOTER //
// ./src/scripts/domain/models/sms_message.js